import moment from "moment";
import Swal from "sweetalert2";
import Strings from "../constants/strings";
import { ASSET_API_URL, Constants } from "../constants/constants";
import { ICodename, IMultiLanguageContent } from "./interfaces";

const Helpers = {
    /**
     * Check Object is null or String null or empty.
     *
     * @param {object | string} value Object or String
     * @returns {boolean} if null or empty return true, otherwise return false.
     */
    isNullOrEmpty: (value: any): value is undefined | boolean => {
        if (Array.isArray(value) && value.length === 0) {
            return true;
        } else {
            return value === undefined || value === null || value === "";
        }
    },

    isFunction: (obj: any): obj is Function => {
        return typeof obj === "function";
    },

    isDev: (): boolean => {
        return process.env.NODE_ENV !== "production";
    },

    getPageNumber: (pageNumber: number, pageSize: number, totalCount: number) => {
        const mathCeil = Math.ceil((totalCount || 0) / pageSize);
        const valTemp = mathCeil > 0 ? mathCeil : 1;
        return pageNumber > valTemp ? valTemp : pageNumber;
    },

    handleFormatParams(data: any) {
        const params = new URLSearchParams();
        Object.entries(data).forEach(([key, values]) => {
            if (!Helpers.isNullOrEmpty(values)) {
                if (Array.isArray(values)) {
                    if (values.length > 0) {
                        values.forEach((value) => {
                            if (!Helpers.isNullOrEmpty(value)) {
                                params.append(key, value.toString());
                            }
                        });
                    }
                } else {
                    params.append(key, `${values}`);
                }
            }
        });
        return params.toString();
    },

    renderExceptionError: (error?: any) => {
        let mesErr = undefined;
        let validationErr = undefined;

        if (error?.message?.responseException?.validationErrors) {
            validationErr = (error?.message?.responseException?.validationErrors || [])[0].message;
        }

        if (error?.message?.responseException?.exceptionMessage) {
            mesErr = error?.message?.responseException?.exceptionMessage || error?.responseException?.exceptionMessage;
        }

        const message: string = (
            mesErr ||
            validationErr ||
            error?.message?.message ||
            error?.message?.Message ||
            Strings.Common.ERROR
        );

        return (message === "Server Error") ? Strings.Common.ERROR : message;
    },

    /**
     * Show confirm alert
     *
     * @param {string} message message for display
     * @param {function} okCallback callback handle when click ok
     * @param {function} cancelCallback callback handle when click cancel
     */
    showConfirmAlert: async (message: string, okCallback: any, cancelCallback?: any, okButtonMessage?: string, cancelButtonMessage?: string) => {
        const msg = message;
        const okPress = await Swal.fire({
            customClass: {
                container: "custom-sweetalert2",
                confirmButton: "btn btn-success border-10",
                cancelButton: "btn btn-danger border-10",
            },
            text: msg,
            icon: "warning",
            cancelButtonColor: "#7b809a",
            confirmButtonColor: "#222224",
            confirmButtonText: okButtonMessage || "Ok",
            cancelButtonText: cancelButtonMessage || Strings.Common.CANCEL,
            showCancelButton: true,
            reverseButtons: true,
        });
        if (okPress && okPress.isConfirmed && okCallback && Helpers.isFunction(okCallback)) {
            okCallback();
        } else {
            if (cancelCallback && Helpers.isFunction(cancelCallback)) {
                cancelCallback();
            }
        }
    },

    renderValueByLanguage: (props: { data?: IMultiLanguageContent; currentLanguage: string }) => {
        if (Helpers.isNullOrEmpty(props.data?.value) || Object.keys(props.data?.value).length === 0) {
            return "";
        } else {
            return Helpers.isNullOrEmpty(props.data?.value?.[props.currentLanguage])
                ? props.data?.value?.[Constants.DefaultLanguage]
                : props.data?.value?.[props.currentLanguage];
        }
    },

    getDataMultiLanguage: (props: { data: IMultiLanguageContent | undefined; currentLanguage: string; listLanguage: ICodename[] }) => {
        if (Helpers.isNullOrEmpty(props.data)) {
            return undefined;
        } else {
            let dataTemp: any = { ...props.data };
            props.listLanguage.forEach((e) => {
                if (Helpers.isNullOrEmpty(dataTemp?.value?.[`${e.code}`])) {
                    const newValue = {
                        ...dataTemp?.value,
                        [`${e.code}`]: dataTemp?.value?.[props.currentLanguage] || "",
                    };
                    dataTemp = { ...dataTemp, value: newValue };
                }
            });
            return dataTemp as IMultiLanguageContent;
        }
    },

    trimSpaceForwardSlash: (inputString: string): string => {
        if (inputString != null && inputString.length > 0) {
            let modifiedString = inputString;
            while (
                !Helpers.isNullOrEmpty(modifiedString) &&
                (modifiedString[0] === " " ||
                    modifiedString[0] === "/" ||
                    modifiedString[modifiedString.length - 1] === " " ||
                    modifiedString[modifiedString.length - 1] === "/")
            ) {
                modifiedString = modifiedString.trim();
                modifiedString = modifiedString.replace(/^\/+/, "").replace(/\/+$/, "");
            }
            return modifiedString;
        } else {
            return "";
        }
    },

    urlJoin: (...parts: (string | undefined | null)[]): string => {
        const urlParts: (string | undefined | null)[] = parts
            .filter((part) => !Helpers.isNullOrEmpty(part) && part && part.length > 0)
            .map((part) => part && Helpers.trimSpaceForwardSlash(part))
            .filter((part) => !Helpers.isNullOrEmpty(part) && part && part.length > 0);

        return urlParts.join("/");
    },

    isIMultiLanguageContent: (obj: any): obj is IMultiLanguageContent => {
        return !Helpers.isNullOrEmpty(obj) && "value" in obj && !Helpers.isNullOrEmpty(obj["value"]) && typeof obj["value"] === "object";
    },

    getDefaultValueMultiLanguage: (data: IMultiLanguageContent | undefined, currentLanguage: string) => {
        if (Helpers.isNullOrEmpty(data)) return "";
        if (Helpers.isNullOrEmpty(data.value?.[currentLanguage]) && !Helpers.isNullOrEmpty(data?.value?.[Constants.DefaultLanguage]))
            return data?.value?.[Constants.DefaultLanguage];
        if (Helpers.isNullOrEmpty(data.value?.[currentLanguage])) return "";
        return data.value?.[currentLanguage];
    },

    handleValidationError: (error: any) => {
        let newErrors: { [key: string]: string } = {};

        if (error.name === "ValidationError") {
            error.inner.forEach((e: any) => {
                const path = `${e.path}`.split(".");
                newErrors[path[0]] = e.errors[0];
            });
        }
        return newErrors;
    },

    getFileAccessUrl: (fileId?: string) => {
        if (Helpers.isNullOrEmpty(fileId) || fileId === "0") {
            return "";
        }
        return ASSET_API_URL + Constants.ApiPath.ASSET.GET_ASSET_ACCESS_URL + `/${fileId}`;
    },

    checkMaxLengthTypeNumber: (value: any, maxLength: number) => {
        let result = value.toString().slice(0, maxLength);
        return result;
    },

    handleTrimSpaceForwardSlash: (inputString: string): string => {
        if (inputString != null && inputString.length > 0) {
            let modifiedString = inputString;
            while (
                !Helpers.isNullOrEmpty(modifiedString) &&
                (modifiedString[0] === " " ||
                    modifiedString[0] === "/" ||
                    modifiedString[modifiedString.length - 1] === " " ||
                    modifiedString[modifiedString.length - 1] === "/")
            ) {
                modifiedString = modifiedString.trim();
                modifiedString = modifiedString.replace(/^\/+/, "").replace(/\/+$/, "");
            }
            return modifiedString;
        } else {
            return "";
        }
    },

    handleUrlJoin: (...parts: (string | undefined | null)[]): string => {
        const urlParts: (string | undefined | null)[] = parts
            .filter((part) => !Helpers.isNullOrEmpty(part) && part && part.length > 0)
            .map((part) => part && Helpers.handleTrimSpaceForwardSlash(part))
            .filter((part) => !Helpers.isNullOrEmpty(part) && part && part.length > 0);

        return urlParts.join("/");
    },

    formatDate: (props: { value?: any; format?: string }) => {
        if (Helpers.isNullOrEmpty(props.value) || props.value === 0 || props.value === "0") {
            return "";
        } else {
            return moment(Number(props.value) * 1000).format(props.format || "DD/MM/YYYY");
        }
    },

    formatDateTime: (value: number) => {
        const dateTime = new Date(value * 1000);
        let h = "" + dateTime.getHours();
        let m = "" + dateTime.getMinutes();
        let s = "" + dateTime.getSeconds();
        let day = "" + dateTime.getDate();
        let month = "" + (dateTime.getMonth() + 1);
        let year = "" + dateTime.getFullYear();

        if (h.length < 2) {
            h = "0" + h;
        }
        if (m.length < 2) {
            m = "0" + m;
        }
        if (s.length < 2) {
            s = "0" + s;
        }
        if (day.length < 2) {
            day = "0" + day;
        }
        if (month.length < 2) {
            month = "0" + month;
        }
        let result = `${day}/${month}/${year} ${h}:${m}:${s}`;
        return result;
    },

    leftDate: (number: number) => {
        const current = new Date().getTime() / 1000;
        const last = number;
        const minutes = (current - last) / 60;
        const hour = minutes / 60;
        const day = hour / 24;
        const month = day / 30;
        const year = month / 12;
        if (year < 5 && year >= 1) return `${Math.round(year)} năm trước`;
        if (month <= 12 && month >= 1) return `${Math.round(month)} tháng trước`;
        if (day <= 31 && day >= 1) return `${Math.round(day)} ngày trước`;
        if (hour < 24 && hour >= 1) return `${Math.round(hour)} giờ trước`;
        if (minutes <= 1 && minutes) return `Vừa mới đây`;
        if (minutes <= 60 && minutes >= 1) return `${Math.round(minutes)} phút trước`;
        return Helpers.formatDateTime(last * 1000);
    },

    unique: (arr: any[]) => {
        let tempObj: any = {};
        arr.map((item) => (tempObj[item.id] = item));
        arr = Object.values(tempObj);
        return arr;
    },

    isEmail: (value: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(value)
    }
};

export default Helpers;
